(() => {
  if (customElements.get('ctfa-quick-order')) return;
  const mobile = window.matchMedia('(max-width: 749px)');
  let active = null;
  let cartRefreshVersion = 0;
  let instanceSequence = 0;

  class CtfaQuickOrder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.trigger = this.querySelector('[data-ctfa-open]');
      this.panel = this.querySelector('[data-ctfa-panel]');
      this.form = this.querySelector('[data-ctfa-form]');
      this.optionsContainer = this.querySelector('[data-ctfa-options]');
      this.quantity = this.querySelector('[data-ctfa-quantity]');
      this.submitButton = this.querySelector('[data-ctfa-submit]');
      this.price = this.querySelector('[data-ctfa-price]');
      this.status = this.querySelector('[data-ctfa-status]');
      if (![this.trigger, this.panel, this.form, this.optionsContainer, this.quantity, this.submitButton, this.price, this.status].every(Boolean)) return;
      if (!this.dataset.productUrl || !/^\d+$/.test(this.dataset.productId || '') || !this.dataset.sectionId || !/^[A-Z]{3}$/.test(this.dataset.currency || '')) return;
      const suffix = `-${++instanceSequence}`;
      const ids = new Map();
      this.querySelectorAll('[id]').forEach(node => {
        const original = node.id;
        node.id = `${original}${suffix}`;
        ids.set(original, node.id);
      });
      this.querySelectorAll('[aria-controls], [aria-labelledby], [aria-describedby], label[for]').forEach(node => {
        ['aria-controls', 'aria-labelledby', 'aria-describedby', 'for'].forEach(attribute => {
          if (node.hasAttribute(attribute)) node.setAttribute(attribute, node.getAttribute(attribute).split(/\s+/).map(id => ids.get(id) || id).join(' '));
        });
      });
      this.initialized = true;
      this.opened = false;
      this.pending = false;
      this.loading = false;
      this.loaded = false;
      this.unsupported = false;
      this.selections = [];
      this.selects = [];
      this.requestVersion = 0;
      this.home = document.createComment('');
      this.panel.before(this.home);
      this.defaultAdd = this.dataset.labelAdd || this.submitButton.textContent.trim();
      this.defaultPrice = this.dataset.labelPrice || this.price.textContent.trim();
      this.trigger.hidden = false;
      if (this.dataset.available === 'false') {
        this.trigger.disabled = true;
        this.trigger.textContent = this.dataset.labelSoldOut || this.trigger.textContent;
      }
      this.trigger.setAttribute('aria-expanded', 'false');
      this.trigger.addEventListener('click', () => this.opened ? this.close() : this.open());
      this.panel.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button || !this.panel.contains(button)) return;
        if (button.hasAttribute('data-ctfa-close')) this.close();
        if (button.hasAttribute('data-ctfa-step')) this.step(Number(button.dataset.ctfaStep));
      });
      this.panel.addEventListener('keydown', event => this.handleKey(event));
      this.optionsContainer.addEventListener('change', event => {
        const select = event.target.closest('[data-ctfa-option]');
        if (!select || this.pending) return;
        const index = Number(select.dataset.ctfaOption);
        this.selections = this.selections.map((value, i) => i < index ? value : i === index ? select.value : '');
        this.selectedVariant = null;
        this.renderOptions();
        this.requestOptions(index);
      });
      this.quantity.addEventListener('input', () => {
        this.quantity.setCustomValidity('');
        this.setStatus('');
        this.updateButtons();
      });
      this.form.addEventListener('submit', event => {
        event.preventDefault();
        event.stopPropagation();
        this.add();
      });
      this.updateVariant();
    }

    disconnectedCallback() {
      this.requestVersion += 1;
      this.requestController?.abort();
      if (this.opened) this.close(false);
    }

    available() {
      return !!(this.loaded && !this.loading && !this.unsupported && this.selectedVariant?.available);
    }

    rules() {
      const rule = this.selectedVariant?.quantityRule;
      return { min: rule?.min || 1, max: rule?.max ?? Infinity, step: rule?.increment || 1 };
    }

    focusOption() {
      return this.selects.find((select, index) => !select.disabled && !this.selections[index]) || this.selects.find(select => !select.disabled) || this.panel.querySelector('[data-ctfa-close]');
    }

    updateVariant() {
      const { min, max, step } = this.rules();
      this.quantity.min = String(min);
      this.quantity.step = String(step);
      if (Number.isFinite(max)) this.quantity.max = String(max);
      else this.quantity.removeAttribute('max');
      this.quantity.value = String(min);
      this.quantity.setCustomValidity('');
      this.price.textContent = this.selectedVariant?.priceText || this.defaultPrice;
      this.updateButtons();
    }

    updateButtons() {
      const available = this.available();
      const { min, max } = this.rules();
      const value = Number(this.quantity.value);
      this.submitButton.disabled = this.pending || !available;
      this.quantity.disabled = this.pending || !available;
      this.selects.forEach((select, index) => {
        select.disabled = this.pending || this.unsupported || this.selections.slice(0, index).some(id => !id) || (this.loading && index > this.loadingIndex);
      });
      this.panel.querySelectorAll('[data-ctfa-step]').forEach(button => {
        button.disabled = this.pending || !available || (Number(button.dataset.ctfaStep) < 0 ? value <= min : value >= max);
      });
      this.submitButton.textContent = this.pending ? this.dataset.labelAdding || this.defaultAdd : this.defaultAdd;
      this.form.setAttribute('aria-busy', String(this.pending || this.loading));
    }

    renderOptions() {
      if (!this.productData) return;
      const options = this.productData.hasOnlyDefaultVariant ? [] : this.productData.options;
      if (this.selects.length !== options.length) {
        this.optionsContainer.replaceChildren();
        this.selects = options.map((option, index) => {
          const field = document.createElement('div');
          field.className = 'ctfa-qo__field';
          const label = document.createElement('label');
          const select = document.createElement('select');
          select.dataset.ctfaOption = String(index);
          select.id = `${this.panel.id}-option-${index}`;
          select.required = true;
          label.htmlFor = select.id;
          label.textContent = option.name;
          field.append(label, select);
          this.optionsContainer.append(field);
          return select;
        });
      }
      options.forEach((option, index) => {
        const select = this.selects[index];
        const blank = document.createElement('option');
        blank.value = '';
        blank.textContent = (this.dataset.labelSelectOption || 'Select [option]').replace('[option]', option.name);
        select.replaceChildren(blank);
        option.values.forEach(value => {
          const node = document.createElement('option');
          node.value = value.id;
          node.textContent = value.name;
          node.disabled = !value.available;
          select.append(node);
        });
        select.value = this.selections[index] || '';
      });
      this.updateButtons();
    }

    validateData(data, requested) {
      if (!data || data.productId !== this.dataset.productId || data.currency !== this.dataset.currency) throw new Error('Invalid product context');
      if (data.eligible !== true) {
        const error = new Error('Unsupported product');
        error.unsupported = true;
        throw error;
      }
      if (typeof data.hasOnlyDefaultVariant !== 'boolean' || !Array.isArray(data.options) || data.options.length < 1 || data.options.length > 3) throw new Error('Invalid options');
      data.options.forEach(option => {
        if (typeof option.name !== 'string' || !option.name || !Array.isArray(option.values) || !option.values.length) throw new Error('Invalid option values');
        const ids = new Set();
        option.values.forEach(value => {
          if (!/^\d+$/.test(value.id) || typeof value.name !== 'string' || typeof value.available !== 'boolean' || ids.has(value.id)) throw new Error('Invalid option value');
          ids.add(value.id);
        });
      });
      const variant = data.selectedVariant;
      if (variant !== null) {
        const rule = variant?.quantityRule;
        if (!variant || !/^\d+$/.test(variant.id) || typeof variant.available !== 'boolean' || typeof variant.priceText !== 'string' || !variant.priceText || !Array.isArray(variant.selectedOptionValueIds)) throw new Error('Invalid variant');
        if (!rule || !Number.isSafeInteger(rule.min) || rule.min < 1 || !Number.isSafeInteger(rule.increment) || rule.increment < 1 || (rule.max !== null && (!Number.isSafeInteger(rule.max) || rule.max < rule.min))) throw new Error('Invalid quantity rules');
        if (variant.selectedOptionValueIds.length !== data.options.length || variant.selectedOptionValueIds.some((id, index) => typeof id !== 'string' || !data.options[index].values.some(value => value.id === id))) throw new Error('Invalid selected options');
      }
      const complete = data.hasOnlyDefaultVariant || (requested.length === data.options.length && requested.every(Boolean));
      const exact = data.hasOnlyDefaultVariant || (variant && variant.selectedOptionValueIds.every((id, index) => id === requested[index]));
      return complete && exact ? variant : null;
    }

    async requestOptions(changedIndex = -1) {
      const version = ++this.requestVersion;
      this.requestController?.abort();
      const controller = new AbortController();
      this.requestController = controller;
      const requested = this.selections.slice();
      const focusedElement = document.activeElement;
      const focusedIndex = this.selects.indexOf(focusedElement);
      this.loading = true;
      this.loadingIndex = changedIndex;
      this.loaded = false;
      this.selectedVariant = null;
      this.setStatus(this.dataset.labelLoading || '', 'loading');
      this.updateVariant();
      const timeout = window.setTimeout(() => controller.abort(), 15000);
      try {
        const url = new URL(this.dataset.productUrl, window.location.origin);
        if (url.origin !== window.location.origin) throw new Error('Invalid product route');
        url.search = '';
        url.searchParams.set('section_id', this.dataset.sectionId);
        const ids = requested.filter(Boolean);
        if (ids.length) url.searchParams.set('option_values', ids.join(','));
        const previewId = new URL(window.location.href).searchParams.get('preview_theme_id');
        if (previewId && /^\d+$/.test(previewId)) url.searchParams.set('preview_theme_id', previewId);
        const response = await fetch(url.href, { credentials: 'same-origin', headers: { Accept: 'text/html' }, cache: 'no-store', signal: controller.signal });
        if (!response.ok) throw new Error('Product data unavailable');
        const html = await response.text();
        if (version !== this.requestVersion || !this.isConnected) return;
        const documentData = new DOMParser().parseFromString(html, 'text/html');
        const node = documentData.querySelector('script[data-ctfa-product-data]');
        if (!node) throw new Error('Missing product data');
        const data = JSON.parse(node.textContent);
        const selectedVariant = this.validateData(data, requested);
        this.productData = data;
        this.selections = data.hasOnlyDefaultVariant ? [] : data.options.map((_, index) => requested[index] || '');
        let invalidSelection = false;
        this.selections = this.selections.map((id, index) => {
          if (invalidSelection || (id && !data.options[index].values.some(value => value.id === id && value.available))) {
            invalidSelection = true;
            return '';
          }
          return id;
        });
        this.selectedVariant = invalidSelection ? null : selectedVariant;
        this.loaded = true;
        this.unsupported = false;
        this.loading = false;
        this.renderOptions();
        this.updateVariant();
        const complete = data.hasOnlyDefaultVariant || this.selections.every(Boolean);
        this.setStatus(invalidSelection || (complete && !this.selectedVariant?.available) ? this.dataset.labelUnavailable || '' : '', invalidSelection || (complete && !this.selectedVariant?.available) ? 'error' : '');
        const initialFocus = changedIndex === -1 && [this.trigger, this.dialog, this.panel.querySelector('[data-ctfa-close]')].includes(document.activeElement);
        if (this.opened && ((focusedIndex >= 0 && document.activeElement === focusedElement) || initialFocus)) (this.selects[focusedIndex] || this.focusOption())?.focus({ preventScroll: true });
      } catch (error) {
        if (version !== this.requestVersion || !this.isConnected) return;
        this.loaded = false;
        this.selectedVariant = null;
        this.unsupported = Boolean(error.unsupported);
        this.setStatus(this.unsupported ? this.dataset.labelUnsupported || '' : this.dataset.labelLoadError || '', 'error');
      } finally {
        window.clearTimeout(timeout);
        if (version === this.requestVersion) {
          this.loading = false;
          this.requestController = null;
          this.updateButtons();
        }
      }
    }

    step(direction) {
      if (this.pending || !this.available() || ![-1, 1].includes(direction)) return;
      const { min, max, step } = this.rules();
      const current = Number(this.quantity.value);
      const index = (current - min) / step;
      const nextIndex = direction > 0 ? Math.floor(index) + 1 : Math.ceil(index) - 1;
      let next = Number.isFinite(current) && this.quantity.value !== '' ? min + nextIndex * step : min;
      const lastValid = Number.isFinite(max) ? min + Math.floor((max - min) / step) * step : max;
      next = Math.max(min, Math.min(lastValid, next));
      if (Number.isFinite(next)) this.quantity.value = String(next);
      this.quantity.setCustomValidity('');
      this.setStatus('');
      this.updateButtons();
    }

    setStatus(message, kind = '') {
      this.status.textContent = message;
      this.status.dataset.state = kind;
      this.status.setAttribute('role', kind === 'error' ? 'alert' : 'status');
    }

    open() {
      if (active && active !== this) active.close(false);
      active = this;
      this.opened = true;
      this.panel.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
      this.setAttribute('data-open', '');
      if (mobile.matches && typeof HTMLDialogElement !== 'undefined' && HTMLDialogElement.prototype.showModal) this.showSheet();
      this.requestOptions();
      this.panel.querySelector('[data-ctfa-close]')?.focus({ preventScroll: true });
    }

    showSheet() {
      if (this.dialog) return;
      const dialog = document.createElement('dialog');
      dialog.className = 'ctfa-qo-dialog';
      const labelledBy = this.panel.getAttribute('aria-labelledby');
      if (labelledBy) dialog.setAttribute('aria-labelledby', labelledBy);
      else dialog.setAttribute('aria-label', this.trigger.textContent.trim());
      this.dialog = dialog;
      document.body.append(dialog);
      dialog.append(this.panel);
      dialog.addEventListener('cancel', event => {
        event.preventDefault();
        this.close();
      });
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
      });
      this.scrollPosition = { x: window.scrollX, y: window.scrollY };
      const bodyStyle = document.body.style;
      this.bodyStyles = {};
      ['position', 'top', 'left', 'width', 'overflow'].forEach(name => {
        this.bodyStyles[name] = { value: bodyStyle.getPropertyValue(name), priority: bodyStyle.getPropertyPriority(name) };
      });
      bodyStyle.setProperty('position', 'fixed');
      bodyStyle.setProperty('top', `${-this.scrollPosition.y}px`);
      bodyStyle.setProperty('left', `${-this.scrollPosition.x}px`);
      bodyStyle.setProperty('width', '100%');
      bodyStyle.setProperty('overflow', 'hidden');
      try {
        dialog.showModal();
      } catch (_) {
        this.hideSheet();
      }
    }

    hideSheet() {
      if (!this.dialog) return;
      const dialog = this.dialog;
      this.dialog = null;
      if (dialog.open) dialog.close();
      this.home.after(this.panel);
      dialog.remove();
      Object.entries(this.bodyStyles || {}).forEach(([name, state]) => {
        if (state.value) document.body.style.setProperty(name, state.value, state.priority);
        else document.body.style.removeProperty(name);
      });
      if (this.scrollPosition) window.scrollTo({ left: this.scrollPosition.x, top: this.scrollPosition.y, behavior: 'instant' });
      this.bodyStyles = null;
      this.scrollPosition = null;
    }

    close(returnFocus = true) {
      this.opened = false;
      this.hideSheet();
      this.panel.hidden = true;
      this.trigger.setAttribute('aria-expanded', 'false');
      this.removeAttribute('data-open');
      if (active === this) active = null;
      if (returnFocus && this.trigger.isConnected) this.trigger.focus({ preventScroll: true });
    }

    handleKey(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.close();
      }
      if (event.key !== 'Tab' || !this.dialog) return;
      const focusable = Array.from(this.panel.querySelectorAll('button:not([disabled]), select:not([disabled]), input:not([disabled]), a[href], [tabindex="0"]')).filter(node => !node.hidden);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    route(endpoint) {
      const root = this.dataset.root || window.Shopify?.routes?.root || '/';
      const url = new URL(`${root.endsWith('/') ? root : `${root}/`}${endpoint}`, window.location.origin);
      if (url.origin !== window.location.origin) throw new Error('Invalid cart route');
      return url.href;
    }

    async refreshCartCount() {
      const version = ++cartRefreshVersion;
      try {
        const response = await fetch(this.route('cart.js'), { credentials: 'same-origin', headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!response.ok) return;
        const cart = await response.json();
        if (version !== cartRefreshVersion || !Number.isInteger(cart.item_count) || cart.item_count < 0) return;
        const cartIcon = document.getElementById('cart-icon-bubble');
        if (!cartIcon) return;
        let bubble = cartIcon.querySelector('.cart-count-bubble');
        if (!cart.item_count) {
          bubble?.remove();
          return;
        }
        if (!bubble) {
          bubble = document.createElement('div');
          bubble.className = 'cart-count-bubble';
          const count = document.createElement('span');
          count.setAttribute('aria-hidden', 'true');
          bubble.append(count);
          const accessible = document.createElement('span');
          accessible.className = 'visually-hidden';
          bubble.append(accessible);
          cartIcon.append(bubble);
        }
        const count = bubble.querySelector('[aria-hidden="true"]');
        if (count) count.textContent = cart.item_count < 100 ? String(cart.item_count) : '';
        const accessible = bubble.querySelector('.visually-hidden');
        if (accessible) accessible.textContent = (this.dataset.labelCartCount || '{{ count }} items').replace('[count]', String(cart.item_count)).replace('{{ count }}', String(cart.item_count));
      } catch (_) {}
    }

    async add() {
      if (this.pending) return;
      if (!this.available()) {
        this.setStatus(this.dataset.labelSelect || this.defaultPrice, 'error');
        this.focusOption()?.focus({ preventScroll: true });
        return;
      }
      const quantity = Number(this.quantity.value);
      const { min, max, step } = this.rules();
      if (!Number.isSafeInteger(quantity) || quantity < min || quantity > max || (quantity - min) % step !== 0) {
        this.quantity.setCustomValidity(this.dataset.labelQuantity || 'Check the quantity.');
        this.quantity.reportValidity();
        this.quantity.focus({ preventScroll: true });
        return;
      }
      const variantId = this.selectedVariant.id;
      if (!/^\d+$/.test(variantId)) return;
      this.pending = true;
      this.setStatus('');
      this.updateButtons();
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch(this.route('cart/add.js'), {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity }] }),
          signal: controller.signal
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          const message = typeof result.description === 'string' ? result.description : typeof result.message === 'string' ? result.message : this.dataset.labelError;
          this.setStatus(message || this.dataset.labelUnconfirmed || 'Unable to add this item.', 'error');
          return;
        }
        const items = Array.isArray(result.items) ? result.items : [result];
        const item = items.find(line => String(line.variant_id || line.id) === variantId && Number.isInteger(line.quantity) && line.quantity >= quantity);
        if (!item) throw new Error('Unconfirmed cart response');
        this.setStatus(this.dataset.labelAdded || 'Added — keep browsing', 'success');
      } catch (_) {
        this.setStatus(this.dataset.labelUnconfirmed || this.dataset.labelError || 'Check your cart before trying again.', 'error');
      } finally {
        window.clearTimeout(timeout);
        this.pending = false;
        this.updateButtons();
        this.refreshCartCount();
      }
    }
  }

  customElements.define('ctfa-quick-order', CtfaQuickOrder);
  mobile.addEventListener('change', () => {
    if (!active?.opened) return;
    const focused = document.activeElement;
    if (mobile.matches) active.showSheet();
    else active.hideSheet();
    if (focused?.isConnected) focused.focus({ preventScroll: true });
  });
})();
